import * as React from "react"
import { graphql, Link, navigate } from 'gatsby'
import { useLocation } from '@reach/router'
import ShowMoreText from "react-show-more-text";
import { useState } from 'react';

// Components
import BreadCrumb from "../components/general/breadcrumb";
import SEO from "../components/seo";
import Tracking from "../components/tracking";
import { SubNavigation } from "../components/sub-navigation"
import YouTubeVideo from "../components/content/youtube";
import ReviewBox from "../components/content/review-box";
import Sidebar from "../components/general/sidebar";

// Functions
import { StripParseHTML } from "../functions/strip-html"
import { sanitizeBrandName } from "../functions/sanitize-string";

// Context
import CartContext from '../context/CartProvider';
import ConfigContext from '../context/ConfigProvider';

// Icons
import Female from "../icons/female"
import Male from "../icons/male"
import Child from "../icons/child"
import Baby from "../icons/baby"
import Eco from "../icons/eco"
import Premium from "../icons/premium"

// Style
import "../styles/category-page.scss"

const NOT_ADD_COPY = [92, 93, 94]

const IconSet = ({ name }) => {
    const icons = {
        herr: <Male color="white" />,
        dam: <Female color="white" />,
        barn: <Child color="white" className="small" />,
        baby: <Baby color="white" className="small" />,
        eco: <Eco className="small eco" />,
        premium: <Premium color="white" className="small premium" />
    };

    const className = (name) => {
        if (name.toLowerCase().includes("herr")) return "herr";
        if (name.toLowerCase().includes("dam")) return "dam";
        if (name.toLowerCase().includes("barn")) return "barn";
        if (name.toLowerCase().includes("baby")) return "baby";
        if (name.toLowerCase().includes("eco")) return "eco";
        if (name.toLowerCase().includes("premium")) return "premium";
    }

    const iconElements = Object.entries(icons).map(([key, icon]) =>
        name.toLowerCase().includes(key) && <div key={`${key}-icon`} title={key.charAt(0).toUpperCase() + key.slice(1)} className={`icon ${className(key)}`}>{icon}</div>
    );

    return <div className="iconSet">{iconElements}</div>;
};

const CategoryPage = ({ location, data: { all: { products }, category, brand } }) => {
    const [sorting, setSorting] = React.useState("low");

    // Query Params
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const brandFilter = queryParams.get('brand');

    // Define brandFilters before using it
    const brandFilters = React.useMemo(() => {
        if (!brandFilter) return [];
        return brandFilter.split('_').map(brand => sanitizeBrandName(decodeURIComponent(brand)));
    }, [brandFilter]);

    // Filter Sidebar States
    const [selectedBrands, setSelectedBrands] = useState(brandFilters);
    const [selectedPriceRange, setSelectedPriceRange] = useState([]);
    const [selectedColors, setSelectedColors] = useState([]);
    const [sidebarActive, setSidebarActive] = useState(false);

    // Cart Context
    const cartContext = React.useContext(CartContext);
    const vatIncluded = cartContext?.state?.vatIncluded ?? false;
    const vatExclAmount = cartContext?.state?.vatExclAmount ?? 0.8;

    // Config Context
    const configContext = React.useContext(ConfigContext);
    const configData = configContext?.configData ?? { categoryNav: [] };

    React.useEffect(() => {
        const scrollToTop = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        setTimeout(scrollToTop, 200);
    }, []);

    const productArray = React.useMemo(() => {
        const array = products.map(obj => obj.product).filter(obj => obj?.custom_url?.url);

        // Apply brand filter
        const filteredArray = brandFilters.length > 0
            ? array.filter(product => {
                const productBrand = brand.nodes.find(b => b.bigcommerce_id === product.brand_id);
                if (productBrand) {
                    const sanitizedBrandName = sanitizeBrandName(productBrand.name);
                    return brandFilters.some(filter => sanitizedBrandName === filter);
                }
                return false;
            })
            : array;

        filteredArray.forEach(product => {
            let discountAmount = Math.max(...product.bulk_pricing_rules.map(obj => obj.amount), 0);
            product.from_price = product.price * ((100 - discountAmount) / 100);
        });

        const sortBy = {
            news: (a, b) => new Date(b.date_created) - new Date(a.date_created),
            popularity: (a, b) => b.is_featured - a.is_featured,
            high: (a, b) => b.from_price - a.from_price,
            low: (a, b) => a.from_price - b.from_price,
        };

        return filteredArray.sort(sortBy[sorting]);
    }, [sorting, products, brandFilters, brand.nodes]);

    const ProductCard = ({ product, index }) => {
        const brandName = brand.nodes.filter(obj => obj.bigcommerce_id === product.brand_id)[0]?.name ?? "";
        return <React.Fragment key={product.id}>
            <div className="productBox">
                <IconSet name={product.name} />
                <Link to={product.custom_url.url} title={`Gå till Tryck på ${product.name}`}>
                    <div className="image">
                        {product.primary_image?.url_standard &&
                            <img src={product.primary_image.url_standard} alt={`Produktbild av ${product.name}`} />}
                    </div>
                    <div className="text">
                        <h3>{product.name}</h3>
                        <p className="description">{StripParseHTML(product.description)}</p>
                        <p className="price">Från {vatIncluded ? product.from_price.toFixed(2) : (product.from_price * vatExclAmount).toFixed(2)} kr</p>
                        <p className="brand">{brandName}</p>
                        <p className="color">Finns i {product.variants.length > 1 ? "flera färger" : "en färg"}</p>
                    </div>
                </Link>
            </div>
            {index === 1 && (
                <YouTubeVideo videoTitle={categoryName} videoId="NYdmTtQt6mg" />
            )}
            {index === 5 && (
                <ReviewBox />
            )}
        </React.Fragment>
    }

    const breadCrumbArray = React.useMemo(() => {
        try {
            return configData.categoryArray.filter(obj => category.parent_id === obj.bigcommerce_id);
        } catch (err) {
            return [];
        }
    }, [configData, category.parent_id]);

    const categoryName = React.useMemo(() => {
        return `${category.name}${!NOT_ADD_COPY.includes(category.bigcommerce_id) ? " med eget tryck" : ""}`
    }, []);

    // Get available brands from products in this category
    const availableBrands = React.useMemo(() => {
        // Get unique brand IDs from the products in this category
        const uniqueBrandIds = [...new Set(
            products
                .map(({ product }) => product.brand_id)
                .filter(Boolean)
        )];

        // Filter the brand list to only include brands that have products
        return brand.nodes
            .filter(brandObj => uniqueBrandIds.includes(brandObj.bigcommerce_id))
            .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
    }, [products, brand.nodes]);

    const handleBrandFilterChange = (newSelectedBrands) => {
        setSelectedBrands(newSelectedBrands);
        
        const params = new URLSearchParams(search);
        
        if (newSelectedBrands.length > 0) {
            // Use _ instead of . as separator
            const brandParam = newSelectedBrands
                .map(brand => sanitizeBrandName(brand))
                .join('_');
            params.set('brand', brandParam);
        } else {
            params.delete('brand');
        }

        navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    };

    const filterData = {
        brands: {
            title: 'Välj varumärken',
            options: availableBrands, // Use available brands instead of all brands
            selected: selectedBrands,
            setSelected: handleBrandFilterChange
        },
        // priceRanges: {
        //     title: 'Välj prisklass',
        //     options: [
        //         { id: 1, name: 'Billig' },
        //         { id: 2, name: 'Mellan' },
        //         { id: 3, name: 'Premium' }
        //     ],
        //     selected: selectedPriceRange,
        //     setSelected: setSelectedPriceRange
        // },
        // colors: {
        //     title: 'Välj färg',
        //     options: [
        //         { id: 1, name: 'Svart' },
        //         { id: 2, name: 'Vit' },
        //         { id: 3, name: 'Röd' },
        //         { id: 4, name: 'Blå' }
        //         // Add more colors as needed
        //     ],
        //     selected: selectedColors,
        //     setSelected: setSelectedColors
        // }
    };

    const toggleSidebar = () => setSidebarActive(!sidebarActive);
    const applyFilters = () => {
        setSidebarActive(false);
    };
    const clearFilters = () => {
        handleBrandFilterChange([]);
        setSidebarActive(false);
    };

    return (
        <>
            <SEO
                noIndex={false}
                locale="sv_SE"
                type="category"
                title={`${category.page_title?.length < 40 ? category.page_title + " | Attitude.se — Experter på tryck sedan 2003" : category.page_title || category.name}`}
                description={category.meta_description || category.description}
                imgSrc={category.image_url || ""}
                url={location.href}
                itemsArray={productArray.map(product => ({
                    name: product.name,
                    description: StripParseHTML(product.description),
                    image: product.primary_image?.url_standard,
                    price: product.price,
                    url: product.custom_url.url,
                }))}
                crumbArray={[
                    ...breadCrumbArray,
                    { name: category.name, url: category.custom_url.url }
                ]}
                webPageSchema={true}
                productSchema={false}
                breadcrumbSchema={true}
                siteNavigationSchema={true}
                localBusinessSchema={true}
                authorSchema={true}
                organizationSchema={true}
                brandSchema={true}
            />
            <Tracking location={location} />
            <BreadCrumb
                location={location}
                breadCrumbArray={breadCrumbArray}
                crumbLabel={`${category.name} med tryck`}
            />
            <Sidebar 
                sidebarActive={sidebarActive} 
                toggleSidebar={toggleSidebar}
                filterData={filterData}
                applyFilters={applyFilters}
                clearFilters={clearFilters}
            />
            <main id="categoryPage">
                <div className="categoryPageWrapper">
                    <SubNavigation location={location} />
                    <div className="productWrapper">
                        <div className="text">
                            <h1>{categoryName}</h1>
                            <ShowMoreText
                                lines={3}
                                more="Läs mer"
                                less="Dölj"
                                className="content"
                                anchorClass="show-more-less-clickable"
                                expanded={false}
                                width={0}
                                truncatedEndingComponent="... "
                            >
                                <h2>{StripParseHTML(category.description)}</h2>
                            </ShowMoreText>
                        </div>
                        {/* <div className="infoBar">
                            <p>
                                <b>Saknar du någon produkt?</b>
                                <br />Vi har över 50,000 artiklar vi kan trycka på.
                            </p>
                            <Link to="/kontakta-oss/" title="Gå till kontakta oss">Kontakta oss</Link>
                        </div> */}
                        <div className="productSort">
                            <p>Sortera på: </p>
                            <div className="ctaWrapper">
                                <select value={sorting} onChange={e => setSorting(e.currentTarget.value)}>
                                    <option value="news">Nyheter</option>
                                    <option value="popularity">Populärast</option>
                                    <option value="low">Pris lågt till högt</option>
                                    <option value="high">Pris högt till lågt</option>
                                </select>
                                <button onClick={toggleSidebar}>Filtrera</button>
                            </div>
                        </div>
                        <div className="productsWrapper">
                            {productArray.map((product, index) => (
                                <ProductCard key={product.id} product={product} index={index} />
                            ))}
                        </div>
                        <div className="missingProducts">
                            <div className="text">
                                <h5>Saknar du något?</h5>
                                <p>Ny hemsida och vi kommer lägga upp alla tusentals produkter i vårt sortiment allt eftersom. Kontakta oss om du saknar något du söker.</p>
                            </div>
                            <Link to="/personlig-service/" title="Gå till Personlig Service">Få personlig service</Link>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default CategoryPage;

export const pageQuery = graphql`
query CategoryPage($id: Int, $categoryId: [Int]) {
  category: bigCommerceCategories(bigcommerce_id: {eq: $id}) {
    id
    bigcommerce_id
    parent_id
    name
    description
    image_url
    page_title
    meta_description
    custom_url {
      url
    }
  }
  brand: allBigCommerceBrands {
    nodes {
      bigcommerce_id
      name
    }
  }
  all: allBigCommerceProducts(
    filter: {categories: {in: $categoryId}, price: {gt: 0}, is_visible: {eq: true}}
  ) {
    products: edges {
      product: node {
        id
        bigcommerce_id
        name
        description
        price
        availability
        is_featured
        view_count
        total_sold
        date_created
        brand_id
        primary_image {
          url_standard
        }
        custom_url {
          url
        }
        bulk_pricing_rules {
          amount
        }
        variants {
          id
        }
      }
    }
  }
}
`;
